
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Dexie from 'dexie';
import API_CONFIG2 from '../components/API_CONFIG/API_CONFIG2';

// Composants de layout (ceux de votre application)
import CunstomFacturesPlacement from '../components/maketApp/layout/CunstomFacturesPlacement';

const db = new Dexie('AppCache');
db.version(1).stores({
  userCache: 'id'
});

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h
const MAX_RETRIES = 3;

// Map pour dédoublonner les requêtes en cours (par userId)
const pendingRequests = new Map();

const FacturesPlacement = () => {
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const abortControllerRef = useRef(null);
  const fetchInProgressRef = useRef(false);

  // 1. Récupération de l'userId depuis localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      console.warn('userId introuvable dans localStorage.');
    }
  }, []);

  // 2. Fonction de récupération avec dédoublonnage et cache
  const fetchUserData = useCallback(async (retryCount = 0) => {
    if (!userId) return;

    // Si une requête est déjà en cours pour ce userId, on attend sa résolution
    if (pendingRequests.has(userId)) {
      console.log('Requête déjà en cours, attente...');
      const data = await pendingRequests.get(userId);
      if (data) {
        setUserData(data);
        localStorage.setItem('userData', JSON.stringify(data));
      }
      return;
    }

    // Création d'un nouvel AbortController pour annuler les requêtes précédentes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchInProgressRef.current = true;

    // Vérification du cache Dexie (frais)
    try {
      const cached = await db.userCache.get(userId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log('Cache Dexie valide');
        setUserData(cached.data);
        localStorage.setItem('userData', JSON.stringify(cached.data));
        fetchInProgressRef.current = false;
        return;
      }
    } catch (error) {
      console.warn('Erreur de lecture du cache Dexie :', error);
    }

    // Vérification du localStorage (fallback)
    try {
      const localData = localStorage.getItem('userData');
      if (localData) {
        const parsed = JSON.parse(localData);
        if (parsed && parsed.id === userId) {
          // On pourrait aussi stocker un timestamp dans localStorage, mais on laisse Dexie gérer
          // On pourrait décider de ne pas utiliser localStorage comme cache principal
        }
      }
    } catch (e) {}

    // Appel API avec dédoublonnage
    const promise = axios.get(
      `${API_CONFIG2.apiUrl}getUserInfo.php?userId=${userId}`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        const data = response.data;
        if (data) {
          setUserData(data);
          localStorage.setItem('userData', JSON.stringify(data));
          await db.userCache.put({
            id: userId,
            data: data,
            timestamp: Date.now()
          });
          return data;
        } else {
          console.warn('Données utilisateur introuvables');
          return null;
        }
      })
      .catch(async (error) => {
        if (axios.isCancel(error)) {
          console.log('Requête annulée');
          return null;
        }
        if (error.response?.status === 429 && retryCount < MAX_RETRIES) {
          console.warn(`429 - Tentative ${retryCount + 1}/${MAX_RETRIES} dans 30s`);
          await new Promise(resolve => setTimeout(resolve, 30000));
          // Nouvelle tentative avec incrément
          return fetchUserData(retryCount + 1);
        }
        console.error('Erreur API :', error);
        // Fallback : utiliser le cache périmé si disponible
        try {
          const fallback = await db.userCache.get(userId);
          if (fallback) {
            setUserData(fallback.data);
            localStorage.setItem('userData', JSON.stringify(fallback.data));
            return fallback.data;
          }
        } catch (dexieError) {
          console.error('Impossible de lire le cache fallback', dexieError);
        }
        return null;
      })
      .finally(() => {
        pendingRequests.delete(userId);
        fetchInProgressRef.current = false;
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      });

    // Stocker la promesse dans le Map pour dédoublonner
    pendingRequests.set(userId, promise);
    const result = await promise;
    return result;
  }, [userId]);

  // 3. Déclenchement de la récupération lorsque userId change
  useEffect(() => {
    if (!userId) return;
    fetchUserData();
    // Nettoyage : annulation de la requête en cours au démontage ou changement d'userId
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // On ne supprime pas la promesse du Map ici, elle sera nettoyée dans son finally
    };
  }, [userId, fetchUserData]);

  // 4. Surveillance du statut "désactivé"
  useEffect(() => {
    if (userData && userData.status === 0) {
      console.log('Compte désactivé, suppression de la session');
      localStorage.removeItem('login');
      window.location.reload();
    }
  }, [userData]);

  // 4. Rendu : affichage conditionnel direct (comme dans le deuxième code)
  return (
    <div className="adminx-container">
      {userData?.fonction === 'SUPER ADMIN' && (
        <CunstomFacturesPlacement userId={userId} fonction={userData.fonction} />
      )}
      {userData?.fonction === 'DSI' && (
        <CunstomFacturesPlacement userId={userId} fonction={userData.fonction} />
      )}
       {userData?.fonction === 'COMPTABLE' && (
        <CunstomFacturesPlacement userId={userId} fonction={userData.fonction} />
      )}
        {userData?.fonction === 'CAISSIER' && (
        <CunstomFacturesPlacement userId={userId} fonction={userData.fonction} />
      )}

    
      
      
      {/* Si aucun rôle ne correspond, on n'affiche rien (ou un message optionnel) */}
      {userData && !['SUPER ADMIN','DSI','RH','FORMATEUR','COMPTABLE','CAISSIER'].includes(userData.fonction) && (
        <div className="text-center mt-5 text-dark">
          Rôle non reconnu ou accès non autorisé. (Fonction : {userData.fonction})
        </div>
      )}
    </div>
  );
};

export default FacturesPlacement;